import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Save, Download, Printer, Check, Package } from "lucide-react";
import { toast } from "sonner";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const PRINT_AREAS = [
  { id: "frontal", label: "Frontal" }, { id: "espalda", label: "Espalda" },
  { id: "frontal_espalda", label: "Frontal y Espalda" }, { id: "completa", label: "Completa" },
];

export default function AdminNewQuotation() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [techniques, setTechniques] = useState([]);
  const [saving, setSaving] = useState(false);
  const [createdId, setCreatedId] = useState(null);

  // Client info
  const [client, setClient] = useState({ name: "", phone: "", email: "", company: "", nit: "CF" });

  // Items
  const [items, setItems] = useState([{ category: "textil", product_id: "", product_name: "", technique_id: "", technique_name: "", quantity: 1, colors: 1, print_area: "frontal", notes: "", is_custom: false, custom_description: "", custom_unit_price: 0 }]);

  // Delivery & advance
  const [deliveryAddress, setDeliveryAddress] = useState("");
  const [deliveryDate, setDeliveryDate] = useState("");
  const [confirmedDate, setConfirmedDate] = useState("");
  const [notes, setNotes] = useState("");
  const [adminNotes, setAdminNotes] = useState("");
  const [requiresAdvance, setRequiresAdvance] = useState(false);
  const [advancePercent, setAdvancePercent] = useState(50);

  // Preview
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    API.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    API.get("/techniques").then((r) => setTechniques(r.data)).catch(() => {});
  }, []);

  const updateClient = (f, v) => setClient((c) => ({ ...c, [f]: v }));

  const updateItem = (idx, field, value) => {
    setItems((prev) => prev.map((it, i) => i === idx ? { ...it, [field]: value } : it));
  };

  const addItem = () => {
    setItems((prev) => [...prev, { category: "textil", product_id: "", product_name: "", technique_id: "", technique_name: "", quantity: 1, colors: 1, print_area: "frontal", notes: "", is_custom: false, custom_description: "", custom_unit_price: 0 }]);
  };

  const removeItem = (idx) => {
    if (items.length <= 1) return;
    setItems((prev) => prev.filter((_, i) => i !== idx));
  };

  const getApplicableTechniques = useCallback((productId) => {
    const p = products.find((pr) => pr.id === productId);
    if (p?.applicable_techniques?.length > 0) return techniques.filter((t) => p.applicable_techniques.includes(t.id));
    return techniques;
  }, [products, techniques]);

  // Fetch preview
  const fetchPreview = useCallback(async () => {
    const valid = items.filter((it) => it.product_id && it.technique_id && it.quantity > 0);
    if (valid.length === 0) { setPreview(null); return; }
    try {
      const { data } = await API.post("/quick-quote", valid.map((it) => ({
        category: it.category, product_id: it.product_id, product_name: it.product_name,
        technique_id: it.technique_id, technique_name: it.technique_name,
        quantity: it.quantity, colors: it.colors, print_area: it.print_area,
        notes: it.notes, is_custom: it.is_custom, custom_description: it.custom_description,
        custom_unit_price: it.custom_unit_price || null,
      })));
      setPreview(data);
    } catch (err) {
      if (process.env.NODE_ENV === "development") console.error("Preview failed:", err.message);
    }
  }, [items]);

  useEffect(() => { fetchPreview(); }, [fetchPreview]);

  const handleSave = async () => {
    if (!client.name) { toast.error("Nombre del cliente es requerido"); return; }
    const validItems = items.filter((it) => it.product_id && it.technique_id);
    if (validItems.length === 0) { toast.error("Agrega al menos un producto"); return; }
    setSaving(true);
    try {
      const { data } = await API.post("/admin/quotations", {
        client_name: client.name, client_email: client.email, client_phone: client.phone,
        company: client.company, nit: client.nit,
        delivery_address: deliveryAddress, delivery_date_requested: deliveryDate || null,
        confirmed_delivery_date: confirmedDate || null,
        notes, admin_notes: adminNotes,
        items: validItems, requires_advance: requiresAdvance, advance_percent: advancePercent,
      });
      setCreatedId(data.id);
      toast.success("Cotización creada");
    } catch (err) {
      toast.error("Error al crear cotización");
    } finally {
      setSaving(false);
    }
  };

  const downloadPdf = async (id) => {
    try {
      const response = await API.get(`/quotations/${id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `cotizacion-${id.slice(0, 8)}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      if (process.env.NODE_ENV === "development") console.error("PDF failed:", err.message);
    }
  };

  const printPdf = async (id) => {
    try {
      const response = await API.get(`/quotations/${id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const w = window.open(url);
      if (w) w.onload = () => w.print();
    } catch (err) {
      if (process.env.NODE_ENV === "development") console.error("Print failed:", err.message);
    }
  };

  const total = preview?.total || 0;
  const advanceAmount = requiresAdvance ? Math.round(total * advancePercent / 100 * 100) / 100 : 0;

  return (
    <div className="p-6 max-w-4xl" data-testid="admin-new-quotation">
      <div className="mb-6">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#0047AB] mb-2">Cotizador Admin</p>
        <h1 className="text-2xl font-bold tracking-tight text-[#0F172A]" style={{ fontFamily: 'Chivo' }}>Nueva Cotización</h1>
        <p className="text-sm text-[#475569] mt-1">Para clientes que contactan por WhatsApp o teléfono</p>
      </div>

      {createdId ? (
        /* Success - show download/print options */
        <div className="bg-white border border-[#E2E8F0] rounded-sm p-8 text-center">
          <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Check className="w-7 h-7 text-green-600" />
          </div>
          <h2 className="text-lg font-bold text-[#0F172A] mb-1" style={{ fontFamily: 'Chivo' }}>Cotización Creada</h2>
          <p className="text-sm text-[#475569] mb-6">Cliente: {client.name} · Total: Q{total.toFixed(2)}</p>
          <div className="flex justify-center gap-3 mb-4">
            <Button onClick={() => downloadPdf(createdId)} className="bg-[#0047AB] text-white font-bold rounded-sm hover:bg-[#003A8C]" data-testid="download-created-pdf">
              <Download className="w-4 h-4 mr-1" /> Descargar PDF
            </Button>
            <Button onClick={() => printPdf(createdId)} variant="outline" className="border-[#0047AB] text-[#0047AB] rounded-sm hover:bg-[#F0F4FF]" data-testid="print-created-pdf">
              <Printer className="w-4 h-4 mr-1" /> Imprimir
            </Button>
          </div>
          <div className="flex justify-center gap-3">
            <Button onClick={() => { setCreatedId(null); setClient({ name: "", phone: "", email: "", company: "", nit: "CF" }); setItems([{ category: "textil", product_id: "", product_name: "", technique_id: "", technique_name: "", quantity: 1, colors: 1, print_area: "frontal", notes: "", is_custom: false, custom_description: "", custom_unit_price: 0 }]); setPreview(null); setNotes(""); setAdminNotes(""); }} variant="ghost" className="text-[#475569] rounded-sm" data-testid="new-another-btn">
              Crear otra cotización
            </Button>
            <Button onClick={() => navigate("/admin/cotizaciones")} variant="ghost" className="text-[#475569] rounded-sm">
              Ver cotizaciones
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-5">
          {/* Client info */}
          <div className="bg-white border border-[#E2E8F0] rounded-sm p-5">
            <h3 className="text-sm font-bold text-[#0F172A] mb-3" style={{ fontFamily: 'Chivo' }}>Datos del Cliente</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-[#475569]">Nombre *</Label>
                <Input value={client.name} onChange={(e) => updateClient("name", e.target.value)} placeholder="Nombre del cliente" className="mt-1 border-[#CBD5E1] rounded-sm" data-testid="admin-client-name" />
              </div>
              <div>
                <Label className="text-xs text-[#475569]">Teléfono</Label>
                <Input value={client.phone} onChange={(e) => updateClient("phone", e.target.value)} placeholder="+502 ..." className="mt-1 border-[#CBD5E1] rounded-sm" data-testid="admin-client-phone" />
              </div>
              <div>
                <Label className="text-xs text-[#475569]">Email</Label>
                <Input value={client.email} onChange={(e) => updateClient("email", e.target.value)} placeholder="email@..." className="mt-1 border-[#CBD5E1] rounded-sm" />
              </div>
              <div>
                <Label className="text-xs text-[#475569]">Empresa</Label>
                <Input value={client.company} onChange={(e) => updateClient("company", e.target.value)} placeholder="Empresa" className="mt-1 border-[#CBD5E1] rounded-sm" />
              </div>
              <div>
                <Label className="text-xs text-[#475569]">NIT</Label>
                <div className="flex gap-2 mt-1">
                  <Input value={client.nit} onChange={(e) => updateClient("nit", e.target.value)} placeholder="NIT" className="border-[#CBD5E1] rounded-sm" />
                  <Button variant="ghost" size="sm" onClick={() => updateClient("nit", "CF")} className={`rounded-sm text-xs shrink-0 ${client.nit === "CF" ? "bg-[#0047AB] text-white" : ""}`}>CF</Button>
                </div>
              </div>
            </div>
          </div>

          {/* Products */}
          <div className="bg-white border border-[#E2E8F0] rounded-sm p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-bold text-[#0F172A]" style={{ fontFamily: 'Chivo' }}>Productos</h3>
              <Button variant="ghost" size="sm" onClick={addItem} className="text-[#0047AB] rounded-sm" data-testid="admin-add-item">
                <Plus className="w-4 h-4 mr-1" /> Agregar
              </Button>
            </div>
            <div className="space-y-4">
              {items.map((it, idx) => {
                const catProducts = products.filter((p) => p.category === it.category);
                const techOptions = it.product_id ? getApplicableTechniques(it.product_id) : techniques;
                const imgUrl = products.find((p) => p.id === it.product_id)?.image_file_id;
                return (
                  <div key={`item-${idx}`} className="border border-[#E2E8F0] rounded-sm p-4" data-testid={`admin-item-${idx}`}>
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-xs font-bold uppercase tracking-wider text-[#0047AB]">Producto {idx + 1}</span>
                      {items.length > 1 && (
                        <Button variant="ghost" size="sm" onClick={() => removeItem(idx)} className="rounded-sm p-1"><Trash2 className="w-4 h-4 text-red-400" /></Button>
                      )}
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      <div>
                        <Label className="text-[10px] text-[#475569] uppercase">Categoría</Label>
                        <Select value={it.category} onValueChange={(v) => { updateItem(idx, "category", v); updateItem(idx, "product_id", ""); updateItem(idx, "product_name", ""); }}>
                          <SelectTrigger className="mt-1 border-[#CBD5E1] rounded-sm h-9 text-sm"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="textil">Textil</SelectItem>
                            <SelectItem value="promocionales">Promocionales</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-[10px] text-[#475569] uppercase">Producto</Label>
                        <Select value={it.product_id} onValueChange={(v) => { const isCustom = v === "custom"; const p = products.find((pr) => pr.id === v); updateItem(idx, "product_id", v); updateItem(idx, "product_name", isCustom ? "Producto Personalizado" : (p?.name || "")); updateItem(idx, "is_custom", isCustom); if (!isCustom) updateItem(idx, "custom_unit_price", 0); }}>
                          <SelectTrigger className="mt-1 border-[#CBD5E1] rounded-sm h-9 text-sm"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                          <SelectContent>
                            {catProducts.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} - Q{p.base_price}</SelectItem>)}
                            <SelectItem value="custom">Personalizado</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-[10px] text-[#475569] uppercase">Técnica</Label>
                        <Select value={it.technique_id} onValueChange={(v) => { const t = techniques.find((tc) => tc.id === v); updateItem(idx, "technique_id", v); updateItem(idx, "technique_name", t?.name || ""); }}>
                          <SelectTrigger className="mt-1 border-[#CBD5E1] rounded-sm h-9 text-sm"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                          <SelectContent>
                            {techOptions.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-[10px] text-[#475569] uppercase">Cantidad</Label>
                        <Input type="number" min="1" value={it.quantity} onChange={(e) => updateItem(idx, "quantity", parseInt(e.target.value) || 1)} className="mt-1 border-[#CBD5E1] rounded-sm h-9 text-sm" />
                      </div>
                      <div>
                        <Label className="text-[10px] text-[#475569] uppercase">Colores</Label>
                        <Input type="number" min="1" max="8" value={it.colors} onChange={(e) => updateItem(idx, "colors", parseInt(e.target.value) || 1)} className="mt-1 border-[#CBD5E1] rounded-sm h-9 text-sm" />
                      </div>
                      {!it.is_custom && (
                        <div>
                          <Label className="text-[10px] text-[#475569] uppercase">Área</Label>
                          <Select value={it.print_area} onValueChange={(v) => updateItem(idx, "print_area", v)}>
                            <SelectTrigger className="mt-1 border-[#CBD5E1] rounded-sm h-9 text-sm"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {PRINT_AREAS.map((a) => <SelectItem key={a.id} value={a.id}>{a.label}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                      <div className="col-span-2">
                        <Label className="text-[10px] text-[#475569] uppercase">Notas del producto</Label>
                        <Input value={it.notes} onChange={(e) => updateItem(idx, "notes", e.target.value)} placeholder="Detalles..." className="mt-1 border-[#CBD5E1] rounded-sm h-9 text-sm" />
                      </div>
                    </div>
                    {it.product_id === "custom" && (
                      <div className="mt-2 space-y-2">
                        <div>
                          <Label className="text-[10px] text-[#475569] uppercase">Descripción del producto personalizado</Label>
                          <Textarea value={it.custom_description} onChange={(e) => updateItem(idx, "custom_description", e.target.value)} placeholder="Describe el artículo..." className="mt-1 border-[#CBD5E1] rounded-sm text-sm" rows={2} />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-[10px] text-[#475569] uppercase">Nombre del producto</Label>
                            <Input value={it.product_name} onChange={(e) => updateItem(idx, "product_name", e.target.value)} placeholder="Ej: Gabachas" className="mt-1 border-[#CBD5E1] rounded-sm h-9 text-sm" />
                          </div>
                          <div>
                            <Label className="text-[10px] text-[#475569] uppercase">Precio unitario (Q)</Label>
                            <Input type="number" step="0.01" min="0" value={it.custom_unit_price || ""} onChange={(e) => updateItem(idx, "custom_unit_price", parseFloat(e.target.value) || 0)} placeholder="0.00" className="mt-1 border-[#CBD5E1] rounded-sm h-9 text-sm" data-testid={`custom-price-${idx}`} />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Delivery & Notes */}
          <div className="bg-white border border-[#E2E8F0] rounded-sm p-5">
            <h3 className="text-sm font-bold text-[#0F172A] mb-3" style={{ fontFamily: 'Chivo' }}>Entrega y Notas</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-[#475569]">Dirección de entrega</Label>
                <Input value={deliveryAddress} onChange={(e) => setDeliveryAddress(e.target.value)} placeholder="Dirección..." className="mt-1 border-[#CBD5E1] rounded-sm" />
              </div>
              <div>
                <Label className="text-xs text-[#475569]">Fecha solicitada</Label>
                <Input type="date" value={deliveryDate} onChange={(e) => setDeliveryDate(e.target.value)} className="mt-1 border-[#CBD5E1] rounded-sm" />
              </div>
              <div>
                <Label className="text-xs text-[#475569]">Fecha confirmada</Label>
                <Input type="date" value={confirmedDate} onChange={(e) => setConfirmedDate(e.target.value)} className="mt-1 border-[#CBD5E1] rounded-sm" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
              <div>
                <Label className="text-xs text-[#475569]">Notas del cliente</Label>
                <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1 border-[#CBD5E1] rounded-sm" rows={2} />
              </div>
              <div>
                <Label className="text-xs text-[#475569]">Notas internas (admin)</Label>
                <Textarea value={adminNotes} onChange={(e) => setAdminNotes(e.target.value)} className="mt-1 border-[#CBD5E1] rounded-sm" rows={2} />
              </div>
            </div>
          </div>

          {/* Advance toggle */}
          <div className="bg-white border border-[#E2E8F0] rounded-sm p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-[#0F172A]">Requiere anticipo</p>
                <p className="text-xs text-[#475569]">Activar si necesita pago anticipado</p>
              </div>
              <Switch checked={requiresAdvance} onCheckedChange={setRequiresAdvance} data-testid="admin-advance-toggle" />
            </div>
            {requiresAdvance && (
              <div className="flex items-center gap-3 mt-3 pt-3 border-t border-[#E2E8F0]">
                <div>
                  <Label className="text-xs text-[#475569]">Porcentaje</Label>
                  <div className="flex items-center gap-1 mt-1">
                    <Input type="number" min="1" max="100" value={advancePercent} onChange={(e) => setAdvancePercent(parseInt(e.target.value) || 50)} className="h-8 w-20 text-sm border-[#CBD5E1] rounded-sm" />
                    <span className="text-sm text-[#475569]">%</span>
                  </div>
                </div>
                {total > 0 && (
                  <div className="text-sm text-[#0F172A]">
                    <p>Anticipo: <strong>Q{advanceAmount.toFixed(2)}</strong></p>
                    <p>Resta: <strong>Q{(total - advanceAmount).toFixed(2)}</strong></p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Preview total */}
          {preview && (
            <div className="bg-[#F0F4FF] border border-[#0047AB]/20 rounded-sm p-4">
              <div className="flex justify-between items-center">
                <span className="text-sm font-bold text-[#0F172A]">Total estimado:</span>
                <span className="text-xl font-bold text-[#E30613]">Q{preview.total?.toFixed(2)}</span>
              </div>
              <p className="text-[10px] text-[#94A3B8] mt-1">Precios incluyen IVA e ISR</p>
            </div>
          )}

          {/* Save button */}
          <Button onClick={handleSave} disabled={saving || !client.name} className="w-full bg-[#E30613] text-white font-bold rounded-sm py-3 hover:bg-[#C80410]" data-testid="admin-save-quotation">
            <Save className="w-4 h-4 mr-2" /> {saving ? "Guardando..." : "Crear Cotización"}
          </Button>
        </div>
      )}
    </div>
  );
}
